/**
 * Tab content for submissions during the submission phase.
 */
import {
    FC,
    MouseEvent,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react'
import { toast } from 'react-toastify'
import _ from 'lodash'
import classNames from 'classnames'
import moment from 'moment'

import { TableLoading } from '~/apps/admin/src/lib'
import { TableMobile } from '~/apps/admin/src/lib/components/common/TableMobile'
import { IsRemovingType } from '~/apps/admin/src/lib/models'
import { MobileTableColumn } from '~/apps/admin/src/lib/models/MobileTableColumn.model'
import { handleError } from '~/apps/admin/src/lib/utils'
import { copyTextToClipboard, useWindowSize, WindowSize } from '~/libs/shared'
import { IconOutline, Table, TableColumn, Tooltip } from '~/libs/ui'

import {
    BackendSubmission,
    ChallengeDetailContextModel,
    convertBackendSubmissionToSubmissionInfo,
    ReviewAppContextModel,
    SubmissionInfo,
} from '../../models'
import { getHandleColor, getProfileUrl } from '../common/columnUtils'
import { TableNoRecord } from '../TableNoRecord'
import { TableWrapper } from '../TableWrapper'
import { SubmissionHistoryModal } from '../SubmissionHistoryModal'
import { useSubmissionDownloadAccess } from '../../hooks/useSubmissionDownloadAccess'
import type { UseSubmissionDownloadAccessResult } from '../../hooks/useSubmissionDownloadAccess'
import { ChallengeDetailContext, ReviewAppContext } from '../../contexts'
import {
    challengeHasSubmissionLimit,
    getSubmissionHistoryKey,
    hasIsLatestFlag,
    partitionSubmissionHistory,
} from '../../utils'
import type { SubmissionHistoryPartition } from '../../utils'
import { TABLE_DATE_FORMAT } from '../../../config/index.config'
import { CollapsibleAiReviewsRow } from '../CollapsibleAiReviewsRow'
import { useRolePermissions, UseRolePermissionsResult } from '../../hooks'
import { SUBMISSION_DOWNLOAD_RESTRICTION_MESSAGE } from '../../constants'
import {
    canReprocessTopgearSubmission,
    reprocessTopgearSubmission,
} from '../../services'
import { ConfirmModal } from '../ConfirmModal'

import { canDownloadSubmissionFromSubmissionsTab } from './submissionDownloadPermissions'
import styles from './TabContentSubmissions.module.scss'

interface Props {
    aiReviewers?: { aiWorkflowId: string }[]
    submissions: BackendSubmission[]
    isLoading: boolean
    isDownloading: IsRemovingType
    downloadSubmission: (submissionId: string) => void
}

const VIRUS_SCAN_FAILED_MESSAGE = 'Submission failed virus scan'

export const TabContentSubmissions: FC<Props> = props => {
    const windowSize: WindowSize = useWindowSize()
    const isTablet = useMemo(
        () => (windowSize.width ?? 0) <= 984,
        [windowSize.width],
    )

    const {
        restrictionMessage,
        isSubmissionDownloadRestrictedForMember,
        getRestrictionMessageForMember,
    }: UseSubmissionDownloadAccessResult = useSubmissionDownloadAccess()
    const { loginUserInfo }: ReviewAppContextModel = useContext(ReviewAppContext)
    const {
        canViewAllSubmissions,
        isAdmin,
        isProjectManager,
        hasCopilotRole,
    }: UseRolePermissionsResult = useRolePermissions()

    const { challengeInfo, registrants }: ChallengeDetailContextModel = useContext(ChallengeDetailContext)
    const [isReprocessingSubmission, setIsReprocessingSubmission] = useState<IsRemovingType>({})
    const [
        pendingReprocessSubmissionId,
        setPendingReprocessSubmissionId,
    ] = useState<string | undefined>(undefined)

    const canShowTopgearReprocess = useMemo(
        () => canReprocessTopgearSubmission(challengeInfo, isAdmin),
        [challengeInfo, isAdmin],
    )

    const isCompletedDesignChallenge = useMemo(() => {
        if (!challengeInfo) return false
        const type = challengeInfo.track.name ? String(challengeInfo.track.name)
            .toLowerCase() : ''
        const status = challengeInfo.status ? String(challengeInfo.status)
            .toLowerCase() : ''
        return type === 'design' && (
            status === 'completed'
        )
    }, [challengeInfo])

    const isSubmissionsViewable = useMemo(() => {
        if (!challengeInfo?.metadata?.length) return false
        return challengeInfo.metadata.some(m => m.name === 'submissionsViewable' && String(m.value)
            .toLowerCase() === 'true')
    }, [challengeInfo])

    const canViewSubmissions = useMemo(() => {
        if (isCompletedDesignChallenge) {
            return canViewAllSubmissions || isSubmissionsViewable
        }

        return true
    }, [isCompletedDesignChallenge, isSubmissionsViewable, canViewAllSubmissions])

    const submissionMetaById = useMemo(
        () => {
            const map = new Map<string, BackendSubmission>()
            props.submissions.forEach(submission => {
                if (submission?.id) {
                    map.set(submission.id, submission)
                }
            })
            return map
        },
        [props.submissions],
    )

    const submissionInfos = useMemo<SubmissionInfo[]>(
        () => props.submissions.map(s => convertBackendSubmissionToSubmissionInfo(s, registrants)),
        [props.submissions, registrants],
    )

    const submissionInfoById = useMemo(
        () => {
            const map = new Map<string, SubmissionInfo>()
            submissionInfos.forEach(submission => {
                if (!submission?.id) {
                    return
                }

                map.set(submission.id, submission)
            })
            return map
        },
        [submissionInfos],
    )

    const submissionHistory = useMemo(
        () => partitionSubmissionHistory(submissionInfos, submissionInfos),
        [submissionInfos],
    )
    const {
        latestSubmissions,
        historyByMember,
    }: SubmissionHistoryPartition = submissionHistory

    const latestBackendSubmissions = useMemo<BackendSubmission[]>(
        () => latestSubmissions
            .map(submission => (submission?.id ? submissionMetaById.get(submission.id) : undefined))
            .filter((submission): submission is BackendSubmission => Boolean(submission)),
        [latestSubmissions, submissionMetaById],
    )

    const restrictToLatest = useMemo(
        () => challengeHasSubmissionLimit(challengeInfo),
        [challengeInfo],
    )

    const hasLatestFlag = useMemo(
        () => hasIsLatestFlag(props.submissions),
        [props.submissions],
    )

    const shouldShowHistoryActions = useMemo(
        () => historyByMember.size > 0,
        [historyByMember],
    )

    const [historyKey, setHistoryKey] = useState<string | undefined>(undefined)

    const historyEntriesForModal = useMemo<SubmissionInfo[]>(
        () => (historyKey ? historyByMember.get(historyKey) ?? [] : []),
        [historyByMember, historyKey],
    )

    const closeHistoryModal = useCallback((): void => {
        setHistoryKey(undefined)
    }, [])

    const openHistoryModalForKey = useCallback(
        (memberId: string | undefined, submissionId: string): void => {
            if (!submissionId) {
                return
            }

            const submissionType = submissionInfoById.get(submissionId)?.type
            const key = getSubmissionHistoryKey(memberId, submissionId, submissionType)
            const entries = historyByMember.get(key) ?? []
            if (!entries.length) {
                return
            }

            setHistoryKey(key)
        },
        [historyByMember, submissionInfoById],
    )

    const handleHistoryButtonClick = useCallback(
        (event: MouseEvent<HTMLButtonElement>): void => {
            const submissionId = event.currentTarget.dataset.submissionId
            if (!submissionId) {
                return
            }

            const memberId = event.currentTarget.dataset.memberId
            openHistoryModalForKey(memberId || undefined, submissionId)
        },
        [openHistoryModalForKey],
    )

    const handleReprocessSubmission = useCallback(
        async (event: MouseEvent<HTMLButtonElement>): Promise<void> => {
            event.stopPropagation()
            event.preventDefault()

            const submissionId = event.currentTarget.dataset.submissionId
            if (!submissionId) {
                return
            }

            setPendingReprocessSubmissionId(submissionId)
        },
        [],
    )

    const closeReprocessConfirmation = useCallback((): void => {
        setPendingReprocessSubmissionId(undefined)
    }, [])

    const handleConfirmReprocessSubmission = useCallback(
        async (): Promise<void> => {
            const submissionId = pendingReprocessSubmissionId
            if (!submissionId) {
                closeReprocessConfirmation()
                return
            }

            const submission = submissionMetaById.get(submissionId)
            if (!submission) {
                toast.error('Submission could not be found for reprocess', {
                    toastId: `topgear-submission-reprocess-${submissionId}`,
                })
                closeReprocessConfirmation()
                return
            }

            setIsReprocessingSubmission(previous => ({
                ...previous,
                [submissionId]: true,
            }))

            try {
                await reprocessTopgearSubmission({
                    submission,
                    submissionInfo: submissionInfoById.get(submissionId),
                })
                toast.success('Reprocess submission request sent', {
                    toastId: `topgear-submission-reprocess-${submissionId}`,
                })
                closeReprocessConfirmation()
            } catch (error) {
                handleError(error as Error)
            } finally {
                setIsReprocessingSubmission(previous => ({
                    ...previous,
                    [submissionId]: false,
                }))
            }
        },
        [
            closeReprocessConfirmation,
            pendingReprocessSubmissionId,
            submissionInfoById,
            submissionMetaById,
        ],
    )

    const resolveSubmissionMeta = useCallback(
        (submissionId: string): SubmissionInfo | undefined => submissionInfoById.get(submissionId),
        [submissionInfoById],
    )

    const getHistoryRestriction = useCallback(
        (submission: SubmissionInfo) => {
            if (!submission.memberId) {
                return { restricted: false }
            }

            const isRestricted = isSubmissionDownloadRestrictedForMember(submission.memberId)
            if (!isRestricted) {
                return { restricted: false }
            }

            return {
                message: getRestrictionMessageForMember(submission.memberId) ?? restrictionMessage,
                restricted: true,
            }
        },
        [
            getRestrictionMessageForMember,
            isSubmissionDownloadRestrictedForMember,
            restrictionMessage,
        ],
    )

    const filteredSubmissions = useMemo<BackendSubmission[]>(
        () => {
            if (restrictToLatest && hasLatestFlag) {
                return latestBackendSubmissions.length
                    ? latestBackendSubmissions
                    : props.submissions
            }

            return props.submissions
        },
        [
            latestBackendSubmissions,
            props.submissions,
            restrictToLatest,
            hasLatestFlag,
            canViewSubmissions,
            loginUserInfo?.userId,
        ],
    )

    const columns = useMemo<TableColumn<BackendSubmission>[]>(
        () => {
            const showEmail = Boolean(isAdmin || isProjectManager || hasCopilotRole)

            const baseColumns: TableColumn<BackendSubmission>[] = [
                {
                    className: styles.submissionColumn,
                    label: 'Submission ID',
                    propertyName: 'id',
                    renderer: (submission: BackendSubmission) => {
                        if (!submission.id) {
                            return <span>-</span>
                        }

                        const isRestrictedBase = isSubmissionDownloadRestrictedForMember(submission.memberId)
                        const normalizedVirusScan = submission.isFileSubmission === false
                            ? undefined
                            : submission.virusScan
                        const failedScan = normalizedVirusScan === false
                        const isOwnSubmission = String(submission.memberId) === String(loginUserInfo?.userId)
                        const canDownloadSubmission = canDownloadSubmissionFromSubmissionsTab({
                            canViewSubmissions,
                            isOwnSubmission,
                        })
                        const isRestricted = isRestrictedBase || failedScan || !canDownloadSubmission
                        let tooltipMessage = failedScan
                            ? VIRUS_SCAN_FAILED_MESSAGE
                            : (
                                getRestrictionMessageForMember(submission.memberId)
                                ?? restrictionMessage
                            )

                        if (!canDownloadSubmission) {
                            tooltipMessage = SUBMISSION_DOWNLOAD_RESTRICTION_MESSAGE
                        }

                        const isButtonDisabled = Boolean(
                            props.isDownloading[submission.id]
                            || isRestricted,
                        )

                        const downloadButton = (
                            <button
                                onClick={function onClick() {
                                    if (isRestricted) {
                                        return
                                    }

                                    props.downloadSubmission(submission.id)
                                }}
                                className={styles.textBlue}
                                disabled={isButtonDisabled}
                                type='button'
                            >
                                {submission.id}
                            </button>
                        )

                        async function handleCopySubmissionId(
                            event: MouseEvent<HTMLButtonElement>,
                        ): Promise<void> {
                            event.stopPropagation()
                            event.preventDefault()

                            await copyTextToClipboard(submission.id)
                            toast.success('Submission ID copied to clipboard', {
                                toastId: `challenge-submission-id-copy-${submission.id}`,
                            })
                        }

                        const renderedDownloadButton = isRestricted ? (
                            <Tooltip content={tooltipMessage} triggerOn='click-hover'>
                                <span className={styles.tooltipTrigger}>
                                    {downloadButton}
                                </span>
                            </Tooltip>
                        ) : (
                            downloadButton
                        )

                        return (
                            <span className={styles.submissionCell}>
                                {renderedDownloadButton}
                                <button
                                    type='button'
                                    className={styles.copyButton}
                                    aria-label='Copy submission ID'
                                    title='Copy submission ID'
                                    onClick={handleCopySubmissionId}
                                    disabled={!submission.id}
                                >
                                    <IconOutline.DocumentDuplicateIcon />
                                </button>
                                {canShowTopgearReprocess && (
                                    <button
                                        type='button'
                                        className={styles.copyButton}
                                        aria-label='Reprocess Topgear submission'
                                        title='Reprocess Topgear submission'
                                        onClick={handleReprocessSubmission}
                                        data-submission-id={submission.id}
                                        disabled={Boolean(isReprocessingSubmission[submission.id])}
                                    >
                                        <IconOutline.RefreshIcon />
                                    </button>
                                )}
                            </span>
                        )
                    },
                    type: 'element',
                },
                ...(showEmail ? [{
                    label: 'Submitter',
                    propertyName: 'memberId',
                    renderer: (submission: BackendSubmission) => {
                        const info = submission.id ? submissionInfoById.get(submission.id) : undefined
                        const userInfo = info?.userInfo
                        const handle = userInfo?.memberHandle?.trim()

                        if (!handle) {
                            return <span>-</span>
                        }

                        const explicitColor = userInfo?.handleColor
                        const maxRating = userInfo?.maxRating ?? userInfo?.rating
                        const resolvedColor = getHandleColor(explicitColor, handle, maxRating) ?? '#2a2a2a'
                        const profileUrl = getProfileUrl(handle)

                        return (
                            <div>
                                <a href={profileUrl} style={{ color: resolvedColor }} target='_blank' rel='noreferrer'>
                                    {handle}
                                </a>
                            </div>
                        )
                    },
                    type: 'element',
                } as TableColumn<BackendSubmission>] : []),
                {
                    label: 'Submitted Date',
                    propertyName: 'submittedDate',
                    renderer: (submission: BackendSubmission) => (
                        <span>
                            {submission.submittedDate
                                ? moment(submission.submittedDate)
                                    .local()
                                    .format(TABLE_DATE_FORMAT)
                                : '-'}
                        </span>
                    ),
                    type: 'element',
                },
                {
                    className: styles.aiReviewerRow,
                    label: 'Reviewer',
                    mobileColSpan: 2,
                    propertyName: 'virusScan',
                    renderer: (submission: BackendSubmission, allRows: BackendSubmission[]) => (
                        submission.isFileSubmission === false ? (
                            <span>N/A</span>
                        ) : (
                            <CollapsibleAiReviewsRow
                                aiReviewers={props.aiReviewers!}
                                submission={submission}
                                defaultOpen={allRows ? !allRows.indexOf(submission) : false}
                            />
                        )
                    ),
                    type: 'element',
                },
            ]

            if (shouldShowHistoryActions) {
                baseColumns.push({
                    label: 'Actions',
                    propertyName: 'actions',
                    renderer: (submission: BackendSubmission) => {
                        if (!submission.id) {
                            return <span>-</span>
                        }

                        const key = getSubmissionHistoryKey(
                            submission.memberId,
                            submission.id,
                            submission.type,
                        )
                        const historyEntries = historyByMember.get(key) ?? []
                        if (!historyEntries.length) {
                            return <span>-</span>
                        }

                        return (
                            <button
                                type='button'
                                className={styles.historyButton}
                                data-submission-id={submission.id}
                                data-member-id={submission.memberId ?? ''}
                                onClick={handleHistoryButtonClick}
                            >
                                View Submission History
                            </button>
                        )
                    },
                    type: 'element',
                })
            }

            return baseColumns
        },
        [
            getRestrictionMessageForMember,
            isSubmissionDownloadRestrictedForMember,
            restrictionMessage,
            props.downloadSubmission,
            props.isDownloading,
            isReprocessingSubmission,
            historyByMember,
            handleHistoryButtonClick,
            handleReprocessSubmission,
            shouldShowHistoryActions,
            canShowTopgearReprocess,
            isAdmin,
            isProjectManager,
            hasCopilotRole,
            submissionInfoById,
            canViewSubmissions,
            loginUserInfo?.userId,
        ],
    )

    const columnsMobile = useMemo<MobileTableColumn<BackendSubmission>[][]>(
        () => columns.map(
            column => [
                {
                    ...column,
                    className: '',
                    label: `${column.label as string} label`,
                    mobileType: 'label',
                    renderer: () => (
                        <div>
                            {column.label as string}
                            :
                        </div>
                    ),
                    type: 'element',
                },
                {
                    ...column,
                    colSpan: column.mobileColSpan,
                    mobileType: 'last-value',
                },
            ] as MobileTableColumn<BackendSubmission>[],
        ),
        [columns],
    )

    if (props.isLoading) {
        return <TableLoading />
    }

    if (!props.submissions.length) {
        return <TableNoRecord message='No submissions' />
    }

    return (
        <TableWrapper className={classNames(styles.container, 'enhanced-table')}>
            {isTablet ? (
                <TableMobile columns={columnsMobile} data={filteredSubmissions} />
            ) : (
                <Table
                    columns={columns}
                    data={filteredSubmissions}
                    disableSorting
                    onToggleSort={_.noop}
                    removeDefaultSort
                />
            )}

            <SubmissionHistoryModal
                open={Boolean(historyKey)}
                onClose={closeHistoryModal}
                submissions={historyEntriesForModal}
                downloadSubmission={props.downloadSubmission}
                isDownloading={props.isDownloading}
                getRestriction={getHistoryRestriction}
                getSubmissionMeta={resolveSubmissionMeta}
                aiReviewers={props.aiReviewers}
            />
            <ConfirmModal
                title='Reprocess Topgear Submission'
                open={Boolean(pendingReprocessSubmissionId)}
                onClose={closeReprocessConfirmation}
                onConfirm={handleConfirmReprocessSubmission}
                cancelText='No'
                action='Yes'
                isLoading={Boolean(
                    pendingReprocessSubmissionId
                    && isReprocessingSubmission[pendingReprocessSubmissionId],
                )}
            >
                <div>
                    Are you sure you want to reprocess this Topgear submission?
                </div>
            </ConfirmModal>
        </TableWrapper>
    )
}

export default TabContentSubmissions
